import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import React from "react";

import { EMPTY_LENGTH } from "../constants/numeric";
import { useChatStore } from "../stores/chatStore";
import { ChatMessage } from "../stores/chatStore";
import { DataChunk } from "../stores/chatTypes";

interface ChunkConfirmationContentProps {
  message: ChatMessage;
}
const CHUNK_INDEX_OFFSET = 1;

interface ChunkListProps {
  totalArtifacts: number;
  chunks: DataChunk[];
}

const ChunkList = ({ totalArtifacts, chunks }: ChunkListProps): React.ReactElement => (
  <Box sx={{ my: 1.5 }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Total artifacts: <strong>{totalArtifacts}</strong>
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Split into <strong>{chunks.length}</strong> chunks:
    </Typography>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1, mb: 2 }}>
      {chunks.map((chunk: DataChunk, index: number) => (
        <Chip
          key={index}
          label={`Chunk ${index + CHUNK_INDEX_OFFSET}: ${chunk.data.length} artifacts`}
          size="small"
          variant="outlined"
          color="info"
        />
      ))}
    </Box>
  </Box>
);

interface ChunkActionsProps {
  onContinue: () => void;
  onStop: () => void;
  loading: boolean;
}

const ChunkActions = ({ onContinue, onStop, loading }: ChunkActionsProps): React.ReactElement => (
  <Box sx={{ display: "flex", gap: 1 }}>
    <Button
      variant="contained"
      color="primary"
      size="small"
      startIcon={<PlayArrowIcon />}
      onClick={onContinue}
      disabled={loading}
    >
      Continue
    </Button>
    <Button
      variant="outlined"
      color="error"
      size="small"
      startIcon={<StopIcon />}
      onClick={onStop}
      disabled={loading}
    >
      Cancel
    </Button>
  </Box>
);

const ChunkConfirmationContent = ({ message }: ChunkConfirmationContentProps): React.ReactElement => {
  const {
    contextData,
    loading,
    continueChunkedQuery,
    setPendingQuery,
    setMessages,
    messages,
    pendingQuery
  } = useChatStore();

  const totalArtifacts = contextData?.length ?? EMPTY_LENGTH;
  const chunks = message.chunks ?? [];
  const isInteractive = pendingQuery && pendingQuery.systemMessageId === message.id;
  const showLoadingForThisMessage = message.isStreaming;

  const handleContinue = async (): Promise<void> => {
    if (continueChunkedQuery) {
      await continueChunkedQuery();
    }
  };

  const handleStop = (): void => {
    setPendingQuery(null);
    const updatedMessages = messages.filter(msg => msg.id !== message.id);
    setMessages(updatedMessages);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {showLoadingForThisMessage && (
          <CircularProgress size={16} sx={{ color: "info.main" }} />
        )}
        <Typography variant="body2">{message.content}</Typography>
      </Box>

      {!showLoadingForThisMessage && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Large datasets will be processed intelligently with automatic token limit handling and hierarchical synthesis if needed.
          </Typography>
          <ChunkList totalArtifacts={totalArtifacts} chunks={chunks} />
          {isInteractive && (
            <ChunkActions onContinue={handleContinue} onStop={handleStop} loading={loading} />
          )}
        </>
      )}
    </Box>
  );
};

export default ChunkConfirmationContent;
